import { useEffect } from "react";
import { Link } from "react-router-dom";

import { Logo } from "../../components/logo.js";

const canonicalUrl = "https://memokite.com/terms";

export function TermsPage(): React.JSX.Element {
  useEffect(() => {
    document.title = "Terms of Service · MemoKite";

    const existingCanonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const canonical = existingCanonical ?? document.createElement("link");
    const previousHref = existingCanonical?.href;
    canonical.rel = "canonical";
    canonical.href = canonicalUrl;
    if (!existingCanonical) document.head.append(canonical);

    return () => {
      if (existingCanonical && previousHref) {
        existingCanonical.href = previousHref;
      } else {
        canonical.remove();
      }
    };
  }, []);

  return (
    <main className="privacy-shell">
      <header className="privacy-header">
        <Link className="brand-link" to="/" aria-label="MemoKite home">
          <Logo />
        </Link>
        <Link className="privacy-header-link" to="/login">
          Log in
        </Link>
      </header>

      <article className="privacy-document">
        <p className="section-kicker">Using MemoKite</p>
        <h1>Terms of Service</h1>
        <p className="privacy-effective-date">Effective 3 August 2026</p>

        <section aria-labelledby="terms-about">
          <h2 id="terms-about">About these Terms</h2>
          <p>
            These Terms of Service govern your use of MemoKite, a service created, operated and
            provided by KNOKODE LIMITED. Please read them before creating an account. Visiting this
            page alone does not form an agreement. You agree to these Terms when you select an email
            or Google signup action after the signup page explains the age requirement and these
            Terms.
          </p>
          <p>
            Our <Link to="/privacy">Privacy Policy</Link> separately explains how we handle personal
            information.
          </p>
          <address className="privacy-contact-card">
            <strong>KNOKODE LIMITED</strong>
            <span>Company number 06813230</span>
            <span>Registered in England and Wales</span>
            <span>Registered office: 4 Vicarage Road</span>
            <span>Teddington, England, TW11 8EZ</span>
            <a href="mailto:memokite-support@knokode.com">memokite-support@knokode.com</a>
          </address>
        </section>

        <section aria-labelledby="terms-eligibility">
          <h2 id="terms-eligibility">Who may use MemoKite</h2>
          <p>
            You must be at least 16 to use MemoKite. If you are under the age at which you can enter
            into a contract where you live, you must also have permission from your parent or legal
            guardian. Selecting a signup action records your declaration; MemoKite does not
            independently verify your age.
          </p>
        </section>

        <section aria-labelledby="terms-service">
          <h2 id="terms-service">The service</h2>
          <p>
            MemoKite is a private learning and planning tool. It lets you organise collections and
            activities, add notes, links and uploads, plan learning and review sessions, use a study
            timer, and view schedules, completion history, your calendar, library and settings.
          </p>
          <p>
            MemoKite helps you organise learning, but it does not guarantee a particular learning,
            academic, professional or other result. Content and suggestions in the service are not
            professional advice. Nothing in these Terms limits rights that the law does not allow us
            to limit.
          </p>
        </section>

        <section aria-labelledby="terms-account">
          <h2 id="terms-account">Your account</h2>
          <p>
            You must provide accurate account information and keep it reasonably current. Keep your
            credentials confidential, use reasonable care to protect your account, and contact us
            promptly if you suspect unauthorised access. You are responsible for activity through
            your account to the extent it results from your failure to take reasonable care.
          </p>
        </section>

        <section aria-labelledby="terms-email">
          <h2 id="terms-email">Email communications and privacy</h2>
          <p>
            KNOKODE LIMITED decides how your account email address is used. MemoKite is the service
            it operates, not a separate company or recipient of your email address.
          </p>
          <p>
            We may use your account email address for MemoKite communications, including email
            verification, account and security notices, support replies, important information about
            the service or changes to these Terms or the Privacy Policy, and other non-promotional
            administrative messages needed to provide MemoKite.
          </p>
          <p>
            KNOKODE LIMITED does not sell or rent account email addresses or disclose them to other
            companies for those companies’ own marketing or independent commercial purposes. Service
            providers may process email addresses on our behalf where necessary to operate MemoKite,
            as explained in the <Link to="/privacy">Privacy Policy</Link>.
          </p>
          <p>
            We do not currently use your account email address to advertise other services provided
            by KNOKODE LIMITED. Before doing so, we would ask for your separate, optional
            permission. Declining or withdrawing that permission would not affect your ability to
            sign up for or use MemoKite, or stop necessary service messages. Accepting these Terms,
            acknowledging the Privacy Policy, signing up by email or Google Sign-In, remaining
            silent, or continuing to use MemoKite does not give marketing permission. Any future
            permission would cover only KNOKODE LIMITED’s own services, not services from
            affiliates, partners or another company.
          </p>
        </section>

        <section aria-labelledby="terms-content">
          <h2 id="terms-content">Your content</h2>
          <p>
            You retain ownership of content you add to MemoKite. You give KNOKODE LIMITED only the
            limited permission needed to host, store, process, back up, reproduce and display that
            content to you, and to operate, secure, legally administer and delete it. This
            permission ends when the content is deleted, subject to the retention and legal
            exceptions explained in the Privacy Policy.
          </p>
          <p>
            This permission does not allow us to sell your content, use it for advertising, publish
            it to other users, or use it to train artificial-intelligence models.
          </p>
        </section>

        <section aria-labelledby="terms-content-rights">
          <h2 id="terms-content-rights">Rights in material you add</h2>
          <p>
            You must own, or have the necessary permission to upload, save, embed or link to, any
            material you add. A saved link does not transfer ownership of the linked material to you
            or to us. If you believe material in MemoKite infringes your rights, contact{" "}
            <a href="mailto:memokite-support@knokode.com">memokite-support@knokode.com</a> with
            enough information for us to investigate.
          </p>
        </section>

        <section aria-labelledby="terms-rules">
          <h2 id="terms-rules">Acceptable use</h2>
          <p>You must not use MemoKite to:</p>
          <ul>
            <li>break the law or infringe another person’s rights;</li>
            <li>upload malware or other harmful code;</li>
            <li>access another person’s account without permission;</li>
            <li>bypass authentication or security controls;</li>
            <li>probe or test the service or its security without our written authorisation;</li>
            <li>interfere with, disrupt or overload the service;</li>
            <li>scrape or carry out bulk extraction of the service or its data;</li>
            <li>impersonate another person or commit fraud; or</li>
            <li>resell or copy MemoKite without our permission.</li>
          </ul>
        </section>

        <section aria-labelledby="terms-third-party">
          <h2 id="terms-third-party">Third-party services and links</h2>
          <p>
            MemoKite may show links you save and YouTube or Vimeo embeds. Third parties operate
            those websites and services under their own terms and privacy practices. We do not
            control, endorse or guarantee them, although we remain responsible for MemoKite and for
            obligations the law places on us.
          </p>
        </section>

        <section aria-labelledby="terms-ip">
          <h2 id="terms-ip">MemoKite intellectual property</h2>
          <p>
            KNOKODE LIMITED and its licensors retain their rights in MemoKite’s software, branding,
            interface and original materials. While these Terms apply, we give you a limited,
            personal, non-transferable right to use MemoKite for its intended purpose. This does not
            give us ownership of your content or third-party material.
          </p>
        </section>

        <section aria-labelledby="terms-payment">
          <h2 id="terms-payment">Free use and future paid plans</h2>
          <p>
            MemoKite is currently free to use, but we do not promise it will remain free. Before you
            purchase a future paid plan, we will disclose its price, billing frequency, renewal and
            cancellation terms, refund terms, and any applicable cooling-off rights. No paid-plan
            terms apply until you choose to purchase after receiving those details.
          </p>
        </section>

        <section aria-labelledby="terms-leaving">
          <h2 id="terms-leaving">Leaving MemoKite and deleting your account</h2>
          <p>
            You may stop using MemoKite at any time. To request deletion of your account and data,
            email <a href="mailto:memokite-support@knokode.com">memokite-support@knokode.com</a>.
            Live account data is removed promptly after an approved request, subject to necessary
            legal and security exceptions. Isolated backup copies are overwritten within 30 days, as
            explained in the Privacy Policy.
          </p>
        </section>

        <section aria-labelledby="terms-suspension">
          <h2 id="terms-suspension">Restriction and suspension</h2>
          <p>
            We may restrict or suspend an account only when reasonably necessary because of a
            material breach of these Terms, a security risk, a risk of serious harm, or a legal
            requirement. Where reasonably practicable, we will give notice and an opportunity to
            respond. We may act urgently when delay would create a material risk. You may ask us to
            review the action by contacting support.
          </p>
        </section>

        <section aria-labelledby="terms-availability">
          <h2 id="terms-availability">Availability and changes to the service</h2>
          <p>
            We use reasonable care and skill in providing MemoKite, but we do not guarantee that it
            will always be uninterrupted or error-free. Maintenance, security incidents, provider
            outages and other events may affect availability. Where reasonably practicable, we will
            give notice before materially discontinuing the service. Your mandatory consumer rights
            remain unaffected.
          </p>
        </section>

        <section aria-labelledby="terms-liability">
          <h2 id="terms-liability">Responsibility and liability</h2>
          <p>
            We do not set a fixed financial liability cap in these Terms. Nothing in these Terms
            excludes or limits liability for fraud or fraudulent misrepresentation, death or
            personal injury caused by negligence, or any statutory or other liability that cannot
            legally be excluded or limited. Any remedies and protections that must apply under your
            local law continue to apply.
          </p>
        </section>

        <section aria-labelledby="terms-law">
          <h2 id="terms-law">Governing law and disputes</h2>
          <p>
            The laws of England and Wales govern these Terms. If you are a consumer elsewhere, this
            choice does not remove mandatory protections given to you by the law where you live or
            your right to use local courts when local law provides that right. You may contact us
            first so we can try to resolve a concern, but doing so is optional and does not restrict
            your legal rights.
          </p>
        </section>

        <section aria-labelledby="terms-changes">
          <h2 id="terms-changes">Changes to these Terms</h2>
          <p>
            We may make minor clarifications and will show the updated date. Before a change that
            materially affects your rights or duties, or when law requires it, we will give advance
            notice by email or in MemoKite and ask you to agree again. A material change will not
            apply retroactively merely because you continued using MemoKite or did not reply.
          </p>
        </section>

        <section aria-labelledby="terms-general">
          <h2 id="terms-general">General</h2>
          <p>
            If a provision cannot be enforced, it will be adjusted or removed only as far as needed,
            and the remaining provisions will continue to apply where lawful. A delay in enforcing a
            provision does not waive it. These Terms do not claim to replace mandatory statutory
            rights or guarantee enforceability in every country.
          </p>
        </section>

        <section aria-labelledby="terms-contact">
          <h2 id="terms-contact">Contact</h2>
          <p>
            Questions about these Terms may be sent to{" "}
            <a href="mailto:memokite-support@knokode.com">memokite-support@knokode.com</a> or to
            KNOKODE LIMITED, 4 Vicarage Road, Teddington, England, TW11 8EZ.
          </p>
        </section>

        <p className="privacy-related-link">
          Read the <Link to="/privacy">MemoKite Privacy Policy</Link>.
        </p>
      </article>
    </main>
  );
}
